import { API_URL, DATECS_BRIDGE_URL } from '../../constants';
import { printOrderReceipt, kioskReceiptPrintingEnabled } from './printReceipt';
import { kioskFiscalEnabled, maybePrintFiscalReceipt } from './fiscalReceipt';
import history from '../../utils/history';

/**
 * Kiosk card payment over a local Datecs terminal (USB).
 *
 * The browser cannot talk to the terminal, so it calls the local bridge agent
 * (DATECS_BRIDGE_URL on 127.0.0.1). The bridge drives the terminal and returns
 * the result; we then confirm it with the TabL backend so the order is
 * finalised. Self-contained DOM overlay — no Redux/router wiring needed, in
 * the spirit of the existing imperative redForm branch.
 */

const T = (translations, key, fallback) => {
  const m = translations && translations.messages && translations.messages.datecs;
  return (m && m[key]) || fallback;
};

function currentSlug() {
  return window.location.pathname.split('/').filter(Boolean)[0] || '';
}

let overlayEl = null;

function ensureOverlay() {
  if (overlayEl) return overlayEl;
  overlayEl = document.createElement('div');
  overlayEl.setAttribute('data-datecs-kiosk', '1');
  Object.assign(overlayEl.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    background: 'rgba(17,17,17,0.96)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '24px',
    fontFamily: 'inherit',
  });
  document.body.appendChild(overlayEl);
  return overlayEl;
}

function removeOverlay() {
  if (overlayEl && overlayEl.parentNode) overlayEl.parentNode.removeChild(overlayEl);
  overlayEl = null;
}

/**
 * Маха overlay-а отвън (киоск ресетът при бездействие). Terminalът не се пипа —
 * използва се само на екран, който чака решение на човек (грешка/отказ).
 */
export function closeDatecsKioskOverlay() {
  removeOverlay();
}

function render({ title, subtitle, spinner, buttons }) {
  const el = ensureOverlay();
  el.innerHTML = '';

  // Маркираме дали в момента ЧАКАМЕ терминала/бекенда (spinner), или екранът
  // чака човек да натисне бутон. Киоск ресетът при бездействие гледа точно това:
  // никога не прекъсва тегленето на картата, но може да разчисти забравен
  // екран с „Опитай отново / Откажи". data-datecs-busy-since служи и за
  // предпазен клапан, ако мостът увисне и spinner-ът остане завинаги.
  el.setAttribute('data-datecs-busy', spinner ? '1' : '0');
  if (spinner) {
    if (!el.getAttribute('data-datecs-busy-since')) {
      el.setAttribute('data-datecs-busy-since', String(Date.now()));
    }
  } else {
    el.removeAttribute('data-datecs-busy-since');
  }

  if (spinner) {
    const sp = document.createElement('div');
    Object.assign(sp.style, {
      width: '54px',
      height: '54px',
      margin: '0 0 28px',
      border: '5px solid rgba(255,255,255,0.25)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'datecs-spin 1s linear infinite',
    });
    el.appendChild(sp);
    if (!document.getElementById('datecs-kiosk-kf')) {
      const st = document.createElement('style');
      st.id = 'datecs-kiosk-kf';
      st.textContent = '@keyframes datecs-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }
  }

  const h = document.createElement('div');
  h.textContent = title;
  Object.assign(h.style, { fontSize: '26px', fontWeight: '700', marginBottom: '10px' });
  el.appendChild(h);

  if (subtitle) {
    const s = document.createElement('div');
    s.textContent = subtitle;
    Object.assign(s.style, { fontSize: '17px', opacity: '0.85', maxWidth: '520px' });
    el.appendChild(s);
  }

  if (buttons && buttons.length) {
    const row = document.createElement('div');
    Object.assign(row.style, { marginTop: '32px', display: 'flex', gap: '14px' });
    buttons.forEach((b) => {
      const btn = document.createElement('button');
      btn.textContent = b.label;
      Object.assign(btn.style, {
        padding: '14px 26px',
        fontSize: '17px',
        fontWeight: '600',
        borderRadius: '10px',
        border: 'none',
        cursor: 'pointer',
        background: b.primary ? '#2e7d32' : 'rgba(255,255,255,0.15)',
        color: '#fff',
      });
      btn.onclick = b.onClick;
      row.appendChild(btn);
    });
    el.appendChild(row);
  }
}

function goToResultPage(status, orderKey) {
  removeOverlay();
  const slug = currentSlug();
  // SPA навигация вместо window.location.assign — без пълно презареждане (и без
  // белия екран за момент). PaymentProcessingPage се рендва от роутера и
  // обработва статуса (getOrders, отваря количката), после връща на менюто.
  history.push(
    `/${slug}/payment?status=${status}&key=${encodeURIComponent(orderKey)}&payment=1`
  );
}

/**
 * @param {{ amount:number, orderKey:string, translations?:object }} opts
 */
export function startDatecsKioskPayment({ amount, orderKey, translations }) {
  const t = (k, f) => T(translations, k, f);

  const fail = (msg, onRetry) => {
    render({
      title: t('failed_title', 'Плащането не бе извършено'),
      subtitle: msg,
      buttons: [
        { label: t('retry', 'Опитай отново'), primary: true, onClick: onRetry },
        {
          label: t('cancel', 'Откажи'),
          onClick: () => goToResultPage('FAILED', orderKey),
        },
      ],
    });
  };

  // Card was charged but the backend confirm failed — never report "failed";
  // keep retrying the confirm only (no second charge).
  const confirmWithRetry = (terminal) => {
    render({
      spinner: true,
      title: t('confirming', 'Потвърждаване на поръчката…'),
      subtitle: t('do_not_close', 'Моля, не затваряйте.'),
    });
    // No credentials: the confirm endpoint authenticates anonymously (menuAuth)
    // and finds the order by uuid. Sending credentials:'include' would force the
    // browser to require Access-Control-Allow-Credentials on the response (which
    // the API does not set), blocking the call cross-origin.
    fetch(API_URL + 'payments/datecs-usb/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderKey, terminal }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok && d && d.success) {
          // Order is paid & finalised. Two independent printouts may follow: the
          // fiscal receipt (SK1-21F, the legal document) and the order slip
          // (SK1-31 or a Windows printer). Each has its own setting, so an
          // object can run either, both or neither. Best-effort in both cases:
          // never block the payment result on a printer.
          const wantSlip = kioskReceiptPrintingEnabled();
          const wantFiscal = kioskFiscalEnabled(d.receipt);
          if (wantSlip || wantFiscal) {
            render({
              spinner: true,
              title: t('printing', 'Отпечатване на бележка…'),
              subtitle: t('do_not_close', 'Моля, не затваряйте.'),
            });
            // Prefer the rich receipt the backend builds; if it's missing (old
            // backend not yet deployed), still print a minimal receipt from what
            // the kiosk already knows, so a print always happens.
            const receipt = d.receipt || {
              orderNumber: '',
              items: [],
              total: amount,
              currency: '€',
              paid: true,
              paymentMethod: 'card',
              dateISO: new Date().toISOString(),
            };
            // Fiscal first — it is the document the customer is entitled to.
            maybePrintFiscalReceipt(receipt, orderKey)
              .then(() => (wantSlip ? printOrderReceipt(receipt, translations) : null))
              .then(() => goToResultPage('PAID', orderKey));
          } else {
            goToResultPage('PAID', orderKey);
          }
        } else {
          fail(
            t(
              'confirm_failed',
              'Картата е приета, но поръчката не бе потвърдена. Опитайте отново или повикайте персонал.'
            ),
            () => confirmWithRetry(terminal)
          );
        }
      })
      .catch(() =>
        fail(
          t(
            'confirm_failed',
            'Картата е приета, но поръчката не бе потвърдена. Опитайте отново или повикайте персонал.'
          ),
          () => confirmWithRetry(terminal)
        )
      );
  };

  const charge = () => {
    render({
      spinner: true,
      title: t('follow_terminal', 'Следвайте инструкциите на терминала'),
      subtitle: t('present_card', 'Поднесете или поставете картата си.'),
    });

    fetch(DATECS_BRIDGE_URL + '/pay', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, ref: orderKey }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.status === 'APPROVED') {
          confirmWithRetry(res);
        } else if (res.status === 'DECLINED') {
          fail(t('declined', 'Картата беше отказана.'), charge);
        } else if (res.status === 'NEEDS_RECOVERY') {
          // Ambiguous on the pinpad — void to be safe, then fail cleanly.
          render({
            spinner: true,
            title: t('cancelling', 'Анулиране на транзакцията…'),
          });
          fetch(DATECS_BRIDGE_URL + '/recover', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 2 }),
          })
            .catch(() => {})
            .finally(() =>
              fail(
                t('recovery', 'Плащането не бе потвърдено и беше анулирано.'),
                charge
              )
            );
        } else {
          fail(t('terminal_error', 'Грешка на терминала.'), charge);
        }
      })
      .catch(() =>
        fail(
          t(
            'no_bridge',
            'Няма връзка с платежния терминал. Повикайте персонал.'
          ),
          charge
        )
      );
  };

  charge();
}
